const NATIVE_AUTH_CAPTCHA_PATH = "/verification";
const NATIVE_AUTH_CAPTCHA_QUERY_PREFIX = "native_auth_captcha=1&nonce=";
const URL_SAFE_NONCE = /^[A-Za-z0-9_-]{64}$/;

const nativeAuthCaptchaPath = (nonce) =>
  `${NATIVE_AUTH_CAPTCHA_PATH}?${NATIVE_AUTH_CAPTCHA_QUERY_PREFIX}${nonce}`;

const isPrivateOrLoopbackHostname = (hostname) => {
  if (hostname === "localhost" || hostname === "::1") {
    return true;
  }

  const ipv4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname);
  if (!ipv4) {
    return false;
  }

  const octets = ipv4.slice(1).map(Number);
  if (octets.some((octet) => octet > 255)) {
    return false;
  }

  const [first, second] = octets;
  return (
    first === 127 ||
    first === 10 ||
    (first === 192 && second === 168) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 169 && second === 254)
  );
};

const isAllowedHttpDevelopmentOrigin = (url, trustedOrigin) => {
  if (url.protocol !== "http:" || !isPrivateOrLoopbackHostname(url.hostname)) {
    return false;
  }
  if (!trustedOrigin) {
    return true;
  }

  try {
    const trusted = new URL(trustedOrigin);
    return (
      trusted.protocol === "http:" &&
      isPrivateOrLoopbackHostname(trusted.hostname) &&
      trusted.port === url.port
    );
  } catch (_error) {
    return false;
  }
};

export const isNativeAuthCaptchaPageProps = (pageProps) =>
  Boolean(pageProps && pageProps.nativeAuthCaptcha);

export const parseNativeAuthCaptchaUrl = (rawUrl) => {
  if (typeof rawUrl !== "string" || rawUrl.length === 0) {
    return null;
  }

  const hashIndex = rawUrl.indexOf("#");
  if (hashIndex !== -1) {
    return null;
  }

  const queryIndex = rawUrl.indexOf("?");
  if (queryIndex === -1) {
    return null;
  }

  const pathname = rawUrl.slice(0, queryIndex);
  const query = rawUrl.slice(queryIndex + 1);
  if (
    pathname !== NATIVE_AUTH_CAPTCHA_PATH ||
    !query.startsWith(NATIVE_AUTH_CAPTCHA_QUERY_PREFIX)
  ) {
    return null;
  }

  const nonce = query.slice(NATIVE_AUTH_CAPTCHA_QUERY_PREFIX.length);
  if (!URL_SAFE_NONCE.test(nonce)) {
    return null;
  }

  return { nonce };
};

// The server cannot observe a URL fragment. Re-check the browser's actual
// location before mounting the native bridge so a hash/navigation race cannot
// turn a generic verification page into a bridge-capable page.
export const isExactNativeAuthCaptchaLocation = (rawUrl, nonce, trustedOrigin) => {
  if (!URL_SAFE_NONCE.test(nonce || "")) {
    return false;
  }

  const expectedPath = nativeAuthCaptchaPath(nonce);
  if (rawUrl === expectedPath) {
    return true;
  }

  try {
    const url = new URL(rawUrl);
    const originAllowed =
      url.protocol === "https:"
        ? !trustedOrigin || url.origin === trustedOrigin
        : isAllowedHttpDevelopmentOrigin(url, trustedOrigin);
    return (
      originAllowed &&
      url.username === "" &&
      url.password === "" &&
      url.pathname === NATIVE_AUTH_CAPTCHA_PATH &&
      url.search === expectedPath.slice(NATIVE_AUTH_CAPTCHA_PATH.length) &&
      url.hash === ""
    );
  } catch (_error) {
    return false;
  }
};

export const buildNativeAuthCaptchaResultUrl = (nonce, captchaVerifyParam) => {
  if (
    !URL_SAFE_NONCE.test(nonce || "") ||
    typeof captchaVerifyParam !== "string" ||
    captchaVerifyParam.trim().length === 0
  ) {
    throw new Error("无效的人机验证结果");
  }

  return `eleduck-auth-captcha://verified?nonce=${encodeURIComponent(
    nonce
  )}&proof=${encodeURIComponent(captchaVerifyParam)}`;
};
